import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, Search, Users, FileText } from "lucide-react";
import PageLayout from "../../components/layout/PageLayout";
import { useAuth } from "../../lib/auth";
import api, { formatApiError } from "../../lib/api";

export default function SponsorDashboard() {
    const { user } = useAuth();
    const [athletes, setAthletes] = useState([]);
    const [proposals, setProposals] = useState([]);
    const [showModal, setShowModal] = useState(null);
    const [pform, setPform] = useState({ campaign_title: "", description: "", proposed_budget: "", deliverables: "" });
    const [q, setQ] = useState("");

    const load = useCallback(() => {
        api.get("/athletes", { params: q ? { q } : {} }).then(r => setAthletes(r.data)).catch(() => {});
        api.get("/proposals").then(r => setProposals(r.data)).catch(() => {});
    }, [q]);

    useEffect(() => { load(); }, [load]);

    const submitProposal = async () => {
        try {
            const payload = {
                athlete_id: showModal.id,
                campaign_title: pform.campaign_title,
                description: pform.description,
                proposed_budget: parseFloat(pform.proposed_budget) || 0,
                deliverables: pform.deliverables.split(",").map(s => s.trim()).filter(Boolean),
            };
            await api.post("/proposals", payload);
            toast.success("Proposal submitted to management for review.");
            setShowModal(null);
            setPform({ campaign_title: "", description: "", proposed_budget: "", deliverables: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <PageLayout>
            <section className="pt-28 pb-8 border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-3">Sponsor Portal</div>
                    <h1 className="font-display text-4xl md:text-5xl text-white tracking-tight" data-testid="sponsor-heading">
                        WELCOME, <span className="text-[#d4af37]">{user.name.toUpperCase()}</span>
                    </h1>
                </div>
            </section>

            <section className="py-10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid md:grid-cols-3 gap-4 mb-10">
                    <div className="surface p-6" data-testid="sponsor-kpi-athletes">
                        <Users className="text-[#d4af37]" size={22} strokeWidth={1.5} />
                        <div className="font-display text-4xl text-white mt-4">{athletes.length}</div>
                        <div className="text-white/40 text-xs uppercase tracking-widest mt-2">Available Athletes</div>
                    </div>
                    <div className="surface p-6" data-testid="sponsor-kpi-proposals">
                        <FileText className="text-[#d4af37]" size={22} strokeWidth={1.5} />
                        <div className="font-display text-4xl text-white mt-4">{proposals.length}</div>
                        <div className="text-white/40 text-xs uppercase tracking-widest mt-2">My Proposals</div>
                    </div>
                    <div className="surface p-6" data-testid="sponsor-kpi-active">
                        <Send className="text-[#d4af37]" size={22} strokeWidth={1.5} />
                        <div className="font-display text-4xl text-white mt-4">{proposals.filter(p => p.status === "accepted").length}</div>
                        <div className="text-white/40 text-xs uppercase tracking-widest mt-2">Accepted</div>
                    </div>
                </div>

                <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                    <div className="section-label mb-5">Browse Athletes</div>
                    <div className="flex items-center gap-3 surface px-4 mb-6 max-w-xl">
                        <Search size={18} className="text-white/50" />
                        <input value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
                            placeholder="Search athletes by name or school..." className="w-full bg-transparent outline-none py-4 text-white placeholder:text-white/40"
                            data-testid="sponsor-search" />
                        <button onClick={load} className="btn-ghost" data-testid="sponsor-search-btn">Go</button>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {athletes.map(a => (
                            <div key={a.id} className="surface surface-hover overflow-hidden" data-testid={`sponsor-athlete-${a.id}`}>
                                <div className="relative aspect-[4/3]">
                                    <img src={a.hero_image} alt={a.name} className="absolute inset-0 w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                                    <div className="absolute bottom-3 left-4 right-4">
                                        <div className="font-display text-xl text-white">{a.name}</div>
                                        <div className="text-white/70 text-xs uppercase tracking-widest">{a.sport} · {a.position} · {a.school}</div>
                                    </div>
                                </div>
                                <div className="p-5 flex items-center justify-between">
                                    <div>
                                        <div className="text-[#d4af37] font-mono text-sm">{(a.social?.followers || 0).toLocaleString()}</div>
                                        <div className="text-white/40 text-[10px] uppercase tracking-widest">followers</div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Link to={`/athletes/${a.id}`} className="btn-outline text-[11px] px-3 py-2" data-testid={`view-athlete-${a.id}`}>View</Link>
                                        <button onClick={() => setShowModal(a)} className="btn-gold text-[11px] px-3 py-2" data-testid={`propose-btn-${a.id}`}>Propose</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-16">
                    <div className="section-label mb-5">My Proposals</div>
                    <div className="surface overflow-x-auto" data-testid="sponsor-proposals-table">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-white/40 text-xs uppercase tracking-widest">
                                    <th className="text-left p-4">Campaign</th>
                                    <th className="text-left p-4">Athlete</th>
                                    <th className="text-right p-4">Budget</th>
                                    <th className="text-left p-4">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {proposals.length === 0 ? (
                                    <tr><td colSpan={4} className="p-8 text-center text-white/40">No proposals yet. Browse athletes above and click Propose.</td></tr>
                                ) : proposals.map(p => (
                                    <tr key={p.id} className="border-t border-white/10">
                                        <td className="p-4 text-white">{p.campaign_title}</td>
                                        <td className="p-4 text-white/70">{p.athlete_id.slice(0,8)}</td>
                                        <td className="p-4 text-right text-[#d4af37]">${p.proposed_budget.toLocaleString()}</td>
                                        <td className="p-4"><span className="chip">{p.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {showModal && (
                <div className="fixed inset-0 bg-black/80 z-[70] flex items-center justify-center p-5" onClick={() => setShowModal(null)}>
                    <div className="surface p-8 max-w-xl w-full" onClick={e => e.stopPropagation()} data-testid="proposal-modal">
                        <div className="section-label mb-3">Propose Campaign</div>
                        <h2 className="font-display text-2xl text-white mb-6">To {showModal.name}</h2>
                        <div className="space-y-4">
                            <div><label className="db-label">Campaign Title</label><input className="db-input-block" value={pform.campaign_title} onChange={e => setPform({...pform, campaign_title: e.target.value})} data-testid="proposal-title" /></div>
                            <div><label className="db-label">Description</label><textarea className="db-input-block db-textarea" value={pform.description} onChange={e => setPform({...pform, description: e.target.value})} data-testid="proposal-desc" /></div>
                            <div><label className="db-label">Proposed Budget (USD)</label><input type="number" className="db-input-block" value={pform.proposed_budget} onChange={e => setPform({...pform, proposed_budget: e.target.value})} data-testid="proposal-budget" /></div>
                            <div><label className="db-label">Deliverables (comma separated)</label><input className="db-input-block" value={pform.deliverables} onChange={e => setPform({...pform, deliverables: e.target.value})} placeholder="1 Reel, 3 Stories, 1 Post" data-testid="proposal-deliverables" /></div>
                        </div>
                        <div className="mt-6 flex gap-3 justify-end">
                            <button onClick={() => setShowModal(null)} className="btn-outline" data-testid="proposal-cancel">Cancel</button>
                            <button onClick={submitProposal} className="btn-gold" data-testid="proposal-submit">Submit Proposal</button>
                        </div>
                    </div>
                </div>
            )}
        </PageLayout>
    );
}
